// @flow

import type { FluxStandardAction } from 'flux-standard-action';
import * as ACTIONS from '../constants/actions';
import * as UserListModel from '../models/user-list';

export type UserStatusKey = 'normal' | 'invited' | 'adopted';

export type EmbeddedIncludeKey = 'preferences' | 'roles';

export type StatusFetchState = {
  ids: number[],
  loading: boolean,
  error: ?string,
  embeddedIncludes: ?$ReadOnlyArray<EmbeddedIncludeKey>,
};

export type ReducerType = {
  byId: { [userId: number]: UserListModel.userT },
  byStatus: {
    normal: StatusFetchState,
    invited: StatusFetchState,
    adopted: StatusFetchState,
  },
};

const emptyStatusState = (): StatusFetchState => ({
  ids: [],
  loading: false,
  error: null,
  embeddedIncludes: null,
});

const INITIAL_STATE: ReducerType = {
  byId: {},
  byStatus: {
    normal: emptyStatusState(),
    invited: emptyStatusState(),
    adopted: emptyStatusState(),
  },
};

const isStatusKey = (s: any): boolean =>
  s === 'normal' || s === 'invited' || s === 'adopted';

export default function usersReducer(
  state: ReducerType = INITIAL_STATE,
  { type, payload }: FluxStandardAction<string, any>,
): ReducerType {
  switch (type) {
    case ACTIONS.USERS_FETCH_REQUEST: {
      if (!payload || !isStatusKey(payload.status)) return state;
      const { status } = payload;
      return {
        ...state,
        byStatus: {
          ...state.byStatus,
          [status]: {
            ...state.byStatus[status],
            loading: true,
            error: null,
          },
        },
      };
    }

    case ACTIONS.USERS_FETCH_SUCCESS: {
      if (!payload || !isStatusKey(payload.status)) return state;
      const { status, users, embeddedIncludes } = payload;
      const list: UserListModel.userT[] = Array.isArray(users) ? users : [];

      const byId = { ...state.byId };
      const ids: number[] = [];
      list.forEach((u) => {
        if (!u || typeof u.id !== 'number') return;
        byId[u.id] = u;
        ids.push(u.id);
      });

      return {
        ...state,
        byId,
        byStatus: {
          ...state.byStatus,
          [status]: {
            ids,
            loading: false,
            error: null,
            embeddedIncludes: Array.isArray(embeddedIncludes)
              ? embeddedIncludes
              : [],
          },
        },
      };
    }

    case ACTIONS.USERS_FETCH_ERROR: {
      if (!payload || !isStatusKey(payload.status)) return state;
      const { status, error } = payload;
      return {
        ...state,
        byStatus: {
          ...state.byStatus,
          [status]: {
            ...state.byStatus[status],
            loading: false,
            error: typeof error === 'string' ? error : 'unknown',
          },
        },
      };
    }

    case ACTIONS.USER_ROLE_ADDED: {
      if (!payload || typeof payload.userId !== 'number' || !payload.role) {
        return state;
      }
      const { userId, role } = payload;
      const existing = state.byId[userId];
      if (!existing) return state;
      const currentRoles = Array.isArray(existing.roles) ? existing.roles : [];
      if (role.id != null && currentRoles.some((r) => r && r.id === role.id)) {
        return state;
      }
      return {
        ...state,
        byId: {
          ...state.byId,
          [userId]: {
            ...existing,
            roles: [...currentRoles, role],
          },
        },
      };
    }

    case ACTIONS.USER_ROLE_REMOVED: {
      if (!payload || typeof payload.userId !== 'number') return state;
      const { userId, roleId } = payload;
      const existing = state.byId[userId];
      if (!existing || !Array.isArray(existing.roles)) return state;
      return {
        ...state,
        byId: {
          ...state.byId,
          [userId]: {
            ...existing,
            roles: existing.roles.filter((r) => r && r.id !== roleId),
          },
        },
      };
    }

    case ACTIONS.USER_PREFERENCE_CREATED: {
      if (!payload || typeof payload.userId !== 'number' || !payload.preference) {
        return state;
      }
      const { userId, preference } = payload;
      const existing = state.byId[userId];
      if (!existing) return state;
      const currentPrefs = Array.isArray(existing.preferences)
        ? existing.preferences
        : [];
      return {
        ...state,
        byId: {
          ...state.byId,
          [userId]: {
            ...existing,
            preferences: [...currentPrefs, preference],
          },
        },
      };
    }

    case ACTIONS.USER_PREFERENCE_UPDATED: {
      if (!payload || typeof payload.userId !== 'number' || !payload.preference) {
        return state;
      }
      const { userId, preference } = payload;
      const existing = state.byId[userId];
      if (!existing) return state;
      const currentPrefs = Array.isArray(existing.preferences)
        ? existing.preferences
        : [];
      const idx = currentPrefs.findIndex(
        (p) => p && preference && p.id === preference.id,
      );
      const nextPrefs =
        idx >= 0
          ? currentPrefs.map((p, i) => (i === idx ? preference : p))
          : [...currentPrefs, preference];
      return {
        ...state,
        byId: {
          ...state.byId,
          [userId]: {
            ...existing,
            preferences: nextPrefs,
          },
        },
      };
    }

    case ACTIONS.USER_PREFERENCE_DELETED: {
      if (!payload || typeof payload.userId !== 'number') return state;
      const { userId, preferenceId } = payload;
      const existing = state.byId[userId];
      if (!existing || !Array.isArray(existing.preferences)) return state;
      return {
        ...state,
        byId: {
          ...state.byId,
          [userId]: {
            ...existing,
            preferences: existing.preferences.filter(
              (p) => p && p.id !== preferenceId,
            ),
          },
        },
      };
    }

    case ACTIONS.USER_REMOVED: {
      if (!payload || typeof payload.userId !== 'number') return state;
      const { userId } = payload;
      const hadRecord = !!state.byId[userId];
      const stillReferenced =
        hadRecord ||
        state.byStatus.normal.ids.includes(userId) ||
        state.byStatus.invited.ids.includes(userId) ||
        state.byStatus.adopted.ids.includes(userId);
      if (!stillReferenced) return state;

      const nextById: { [userId: number]: UserListModel.userT } = {};
      Object.keys(state.byId).forEach((key) => {
        const id = Number(key);
        if (id !== userId) nextById[id] = state.byId[id];
      });

      const filterIds = (s: StatusFetchState): StatusFetchState =>
        s.ids.includes(userId)
          ? { ...s, ids: s.ids.filter((id) => id !== userId) }
          : s;
      return {
        ...state,
        byId: nextById,
        byStatus: {
          normal: filterIds(state.byStatus.normal),
          invited: filterIds(state.byStatus.invited),
          adopted: filterIds(state.byStatus.adopted),
        },
      };
    }

    case ACTIONS.USERS_RESET:
    case ACTIONS.LOGOUT:
      return INITIAL_STATE;

    default:
      return state;
  }
}
